export type RetryImportChunkOptions = {
  retries?: number;
  interval?: number;
  backoffFactor?: number;
  maxInterval?: number;
  jitter?: 'none' | 'full' | 'equal';
  onRetry?: (
    attempt: number,
    error: unknown,
    nextDelayMs: number,
    remaining: number,
  ) => void;
  abortSignal?: AbortSignal;
  reloadOnFail?: boolean;
  reloadCacheBust?: boolean;
  reloadStorageKey?: string;
  reloadGuardMs?: number;
  shouldReload?: (error: unknown) => boolean;
};

export function retryImportChunk<T>(fn: () => Promise<T>): Promise<T>;
export function retryImportChunk<T>(
  fn: () => Promise<T>,
  retries: number,
): Promise<T>;
export function retryImportChunk<T>(
  fn: () => Promise<T>,
  retries: number,
  interval: number,
): Promise<T>;
export function retryImportChunk<T>(
  fn: () => Promise<T>,
  options: RetryImportChunkOptions,
): Promise<T>;

export function retryImportChunk<T>(
  fn: () => Promise<T>,
  retriesOrOptions?: number | RetryImportChunkOptions,
  intervalLegacy?: number,
): Promise<T> {
  const defaults: Required<
    Omit<RetryImportChunkOptions, 'onRetry' | 'abortSignal' | 'shouldReload'>
  > & {
    onRetry?: RetryImportChunkOptions['onRetry'];
    abortSignal?: RetryImportChunkOptions['abortSignal'];
    shouldReload: NonNullable<RetryImportChunkOptions['shouldReload']>;
  } = {
    retries: 5,
    interval: 1000,
    backoffFactor: 2,
    maxInterval: 30000,
    jitter: 'full',
    onRetry: undefined,
    abortSignal: undefined,
    reloadOnFail: true,
    reloadCacheBust: true,
    reloadStorageKey: '__retryImportChunkReloaded__',
    reloadGuardMs: 10000,
    shouldReload: (error: unknown): boolean => {
      if (!error) {
        return false;
      }

      const name = (error as any)?.name ?? '';
      const message = (error as any)?.message ?? '';
      const combined = `${name} ${message}`.toLowerCase();

      return (
        /chunkloaderror/.test(combined) ||
        /loading chunk \d+ failed/.test(combined) ||
        /dynamic import/i.test(combined) ||
        /importing a module script/i.test(combined)
      );
    },
  };

  const options: typeof defaults = (() => {
    if (typeof retriesOrOptions === 'number') {
      return {
        ...defaults,
        retries: retriesOrOptions,
        interval:
          typeof intervalLegacy === 'number'
            ? intervalLegacy
            : defaults.interval,
      };
    }

    if (typeof retriesOrOptions === 'object' && retriesOrOptions !== null) {
      return {
        ...defaults,
        ...retriesOrOptions,
        retries: Math.max(
          1,
          Math.floor(retriesOrOptions.retries ?? defaults.retries),
        ),
        interval: Math.max(
          0,
          Math.floor(retriesOrOptions.interval ?? defaults.interval),
        ),
        backoffFactor: Math.max(
          1,
          retriesOrOptions.backoffFactor ?? defaults.backoffFactor,
        ),
        maxInterval: Math.max(
          0,
          Math.floor(retriesOrOptions.maxInterval ?? defaults.maxInterval),
        ),
        jitter: retriesOrOptions.jitter ?? defaults.jitter,
        reloadGuardMs: Math.max(
          0,
          Math.floor(retriesOrOptions.reloadGuardMs ?? defaults.reloadGuardMs),
        ),
        shouldReload: retriesOrOptions.shouldReload ?? defaults.shouldReload,
      };
    }

    return { ...defaults };
  })();

  const isAborted = (): boolean => {
    try {
      return Boolean(options.abortSignal?.aborted);
    } catch {
      return false;
    }
  };

  const abortReason = (): unknown => {
    try {
      return (options.abortSignal as any)?.reason ?? new Error('Aborted');
    } catch {
      return new Error('Aborted');
    }
  };

  const schedule = (
    fnToRun: () => void,
    delayMs: number,
  ): number | undefined => {
    try {
      return window.setTimeout(fnToRun, delayMs);
    } catch {
      return (setTimeout as any)(fnToRun, delayMs);
    }
  };

  const clearSchedule = (id: number | undefined) => {
    if (typeof id === 'number') {
      try {
        window.clearTimeout(id);
      } catch {
        clearTimeout(id as any);
      }
    }
  };

  const computeNextDelay = (currentDelay: number): number => {
    const base = Math.min(options.maxInterval, Math.max(0, currentDelay));
    const multiplied = Math.min(
      options.maxInterval,
      Math.floor(base * options.backoffFactor),
    );

    switch (options.jitter) {
      case 'none':
        return multiplied;
      case 'equal': {
        const half = Math.floor(multiplied / 2);

        return half + Math.floor(Math.random() * Math.max(1, half));
      }
      case 'full':
      default:
        return Math.floor(Math.random() * Math.max(1, multiplied));
    }
  };

  const reloadWithCacheBust = (): boolean => {
    try {
      const { location, sessionStorage } = window;
      const now = Date.now();
      const key = options.reloadStorageKey;
      const last = Number(sessionStorage.getItem(key) || 0);

      if (last && now - last < options.reloadGuardMs) {
        return false;
      }

      sessionStorage.setItem(key, String(now));

      let href = location.href;

      if (options.reloadCacheBust) {
        const url = new URL(href);

        url.searchParams.set('v', String(now));
        href = url.toString();
      }

      location.replace(href);

      return true;
    } catch {
      try {
        window.location.reload();

        return true;
      } catch {
        return false;
      }
    }
  };

  const run = (remaining: number, delay: number, attempt: number): Promise<T> =>
    new Promise<T>((resolve, reject) => {
      if (isAborted()) {
        reject(abortReason());

        return;
      }

      fn()
        .then(resolve)
        .catch((error: unknown) => {
          if (remaining <= 1) {
            if (options.reloadOnFail && options.shouldReload(error)) {
              reloadWithCacheBust();
            }
            reject(error);

            return;
          }

          const nextDelay = computeNextDelay(delay);

          try {
            options.onRetry?.(attempt, error, nextDelay, remaining - 1);
          } catch {
            // ignore onRetry errors
          }

          const timeoutId = schedule(() => {
            run(remaining - 1, nextDelay, attempt + 1).then(resolve, reject);
          }, delay);

          if (options.abortSignal) {
            const onAbort = () => {
              clearSchedule(timeoutId as number);
              reject(abortReason());
            };

            try {
              options.abortSignal.addEventListener('abort', onAbort, {
                once: true,
              } as any);
            } catch {
              // ignore if addEventListener not available
            }
          }
        });
    });

  return run(options.retries, options.interval, 1);
}
